/**
 * Keeps an open window on the deployed version. The service worker takes over a new version at
 * once (skipWaiting + clients.claim), but the page keeps running the code it loaded, so an open
 * tab or installed app could stay on an old build indefinitely. On `controllerchange`:
 * a hidden page reloads now; a visible one is told (the Reload pill) and reloads the next time
 * it is hidden, so nothing the user is typing is lost. Also checks for a new version periodically.
 *
 * A "Just this session" page is never reloaded automatically: its keys live in memory only, so a
 * reload ends the session (lib/session-only.ts). It only shows the Reload pill, which says so.
 */

import { isSessionOnly } from './session-only.js'

export const UPDATE_READY_EVENT = 'viko:update-ready'
const CHECK_EVERY_MS = 30 * 60_000

export interface UpdateDeps {
  reg: { update: () => Promise<unknown> }
  /** `navigator.serviceWorker`. */
  sw: EventTarget
  doc: EventTarget & { visibilityState: DocumentVisibilityState }
  /** The page was already controlled at load: a later controller is an update, not the install. */
  hadController: boolean
  reload: () => void
  onReady: () => void
  checkEveryMs?: number
  /** True while a reload would lose state the user cannot get back (session-only keys). */
  keepPage?: () => boolean
}

export function applyUpdates(d: UpdateDeps): () => void {
  let pending = false
  const onChange = () => {
    if (!d.hadController) return
    if (d.doc.visibilityState === 'hidden' && !d.keepPage?.()) return d.reload()
    if (!pending) d.onReady()
    pending = true
  }
  const onVisibility = () => {
    if (pending && d.doc.visibilityState === 'hidden' && !d.keepPage?.()) d.reload()
  }
  d.sw.addEventListener('controllerchange', onChange)
  d.doc.addEventListener('visibilitychange', onVisibility)
  // A hidden page never checks (no background requests); the next visible tick does.
  const timer = setInterval(() => {
    if (d.doc.visibilityState !== 'hidden') void d.reg.update().catch(() => {})
  }, d.checkEveryMs ?? CHECK_EVERY_MS)
  return () => {
    d.sw.removeEventListener('controllerchange', onChange)
    d.doc.removeEventListener('visibilitychange', onVisibility)
    clearInterval(timer)
  }
}

/** Browser wiring: announces a ready update with a window event the Reload pill listens for. */
export function applyUpdatesInBrowser(reg: ServiceWorkerRegistration): () => void {
  return applyUpdates({
    reg,
    sw: navigator.serviceWorker,
    doc: document,
    hadController: !!navigator.serviceWorker.controller,
    reload: () => window.location.reload(),
    onReady: () => window.dispatchEvent(new Event(UPDATE_READY_EVENT)),
    keepPage: isSessionOnly,
  })
}
